/**
 * useTasksCalendar · tareas con due_at dentro de un rango [from, to].
 *
 * Range-based (no month-based) para soportar las vistas Mes / Semana / Día:
 * cada vista calcula su rango visible y este hook trae las tareas de ese rango.
 * El frontend agrupa por día y filtra (área/prioridad/estado) en cliente.
 */
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { TaskScope, TasksRow } from '@/types/database.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

interface Args {
  /** Inicio del rango (inclusive · se normaliza a las 00:00 de ese día). */
  from: Date;
  /** Fin del rango (inclusive · se normaliza a las 23:59 de ese día). */
  to: Date;
  scope?: TaskScope;
}

function isoStartOfDay(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
}
function isoEndOfDay(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
}

export function useTasksCalendar({ from, to, scope }: Args) {
  const fromIso = isoStartOfDay(from);
  const toIso = isoEndOfDay(to);
  return useQuery({
    // Key HIJA de ['core','tasks'] → las invalidaciones de las mutaciones y del
    // realtime (que invalidan ['core','tasks']) refrescan el calendario solas,
    // sin refresh manual. (Antes era ['core','tasks-calendar',...] → no matcheaba.)
    queryKey: ['core', 'tasks', 'calendar', fromIso, toIso, scope ?? 'all'],
    queryFn: async (): Promise<TasksRow[]> => {
      let q = db
        .from('tasks')
        .select('*')
        .not('due_at', 'is', null)
        .gte('due_at', fromIso)
        .lte('due_at', toIso)
        .is('deleted_at', null)
        .is('parent_task_id', null) // subtareas no se muestran en el calendar principal
        .neq('status', 'archived')
        .order('due_at', { ascending: true });
      // El tab filtra por scope, PERO las reuniones adoptadas (pinned) se ven en
      // cualquier tab: son personales y el evento sale del overlay al adoptarse,
      // así que sin esto "desaparecerían" del calendario del tab actual.
      if (scope) q = q.or(`scope.eq.${scope},metadata->>calendar_pinned.eq.true`);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as TasksRow[];
    },
    staleTime: 60_000,
  });
}
